import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

/**
 * Export report data to PDF using jsPDF (no screenshot).
 * @param {Object} options
 * @param {string} options.title - Report title
 * @param {string} options.companyName - Company name
 * @param {string} options.period - Period text e.g. "01/01/2024 – 31/01/2024"
 * @param {Array<{id: string, label: string}>} options.columns
 * @param {Array<Object>} options.rows - Array of row objects keyed by column id
 * @param {Array<{label: string, value: string}>} options.summaryItems
 * @param {string} [options.filename] - Download filename without extension
 */
export function exportReportToPdf({
  title,
  companyName,
  period,
  columns = [],
  rows = [],
  summaryItems = [],
  filename = 'report',
}) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  // Premium design palettes
  const colorPrimary = [15, 23, 42];      // Midnight/Deep Navy
  const colorAccent = [37, 99, 235];      // Royal Blue Accent
  const colorTextDark = [30, 41, 59];     // Slate Dark for primary text
  const colorTextMuted = [100, 116, 139]; // Slate Muted for secondary text
  const colorBgCard = [248, 250, 252];    // Off-white / light slate card background
  const colorBorder = [226, 232, 240];    // Clean thin border color

  let y = 14;

  // Decorative Top Accent Bar
  doc.setFillColor(colorAccent[0], colorAccent[1], colorAccent[2]);
  doc.rect(0, 0, pageWidth, 3, 'F');

  // --- REPORT HEADER SECTION ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(colorAccent[0], colorAccent[1], colorAccent[2]);
  doc.text((title || 'Report').toUpperCase(), margin, y + 5);

  // Left Column Metadata: Company & Period
  let metaLeftY = y + 10;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(colorTextMuted[0], colorTextMuted[1], colorTextMuted[2]);
  
  if (companyName) {
    doc.text(companyName, margin, metaLeftY);
    metaLeftY += 4.5;
  }
  if (period) {
    doc.text(`Period: ${period}`, margin, metaLeftY);
    metaLeftY += 4.5;
  }

  // Right Column Metadata: Generated Date/Time
  const rightMetaX = pageWidth - margin;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(colorTextMuted[0], colorTextMuted[1], colorTextMuted[2]);
  
  const currentDateStr = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const currentTimeStr = new Date().toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
  
  doc.text(`Generated: ${currentDateStr} at ${currentTimeStr}`, rightMetaX, y + 5, { align: 'right' });
  doc.text('Format: A4 Landscape', rightMetaX, y + 9, { align: 'right' });

  y = Math.max(metaLeftY + 4, y + 15);

  // --- SUMMARY ITEMS HORIZONTAL DASHBOARD GRID ---
  if (summaryItems && summaryItems.length > 0) {
    const summaryCardHeight = 16;
    doc.setFillColor(colorBgCard[0], colorBgCard[1], colorBgCard[2]);
    doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
    doc.setLineWidth(0.3);
    doc.roundedRect(margin, y, pageWidth - margin * 2, summaryCardHeight, 1.5, 1.5, 'FD');

    // Royal Blue vertical accent bar
    doc.setFillColor(colorAccent[0], colorAccent[1], colorAccent[2]);
    doc.rect(margin, y, 1.5, summaryCardHeight, 'F');

    const itemWidth = (pageWidth - margin * 2 - 4) / summaryItems.length;
    summaryItems.forEach((s, idx) => {
      const itemX = margin + 6 + idx * itemWidth;
      
      // Draw label
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(colorTextMuted[0], colorTextMuted[1], colorTextMuted[2]);
      doc.text((s.label || '').toUpperCase(), itemX, y + 5.5);

      // Draw value
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(colorPrimary[0], colorPrimary[1], colorPrimary[2]);
      doc.text(String(s.value || '-'), itemX, y + 11);
    });
    
    y += summaryCardHeight + 8;
  }

  // --- REPORT DATA TABLE ---
  if (columns.length > 0 && rows.length > 0) {
    const headers = columns.map((c) => c.label);
    const tableData = rows.map((row) => columns.map((col) => {
      const v = row[col.id];
      if (v == null || v === '') return '–';
      if (typeof v === 'number' && !Number.isInteger(v)) {
        return Number(v).toFixed(2);
      }
      return String(v);
    }));

    // Dynamically identify and set right alignment for numeric/financial columns
    const columnStyles = {};
    columns.forEach((col, idx) => {
      const label = (col.label || '').toLowerCase();
      const id = (col.id || '').toLowerCase();
      const numericKeywords = ['amount', 'total', 'price', 'qty', 'count', 'rate', 'vat', 'tax', 'balance', 'sales', 'cost', 'margin', 'profit', 'discount', 'quantity', 'subtotal', 'value', 'debit', 'credit', 'received', 'paid', 'cash', 'variance', 'diff'];
      
      let isNumeric = numericKeywords.some(kw => label.includes(kw) || id.includes(kw));
      
      if (!isNumeric) {
        // Fallback: check if the first non-empty value in this column is numeric
        for (const r of rows) {
          const val = r[col.id];
          if (val !== null && val !== undefined && val !== '') {
            if (typeof val === 'number' || (!isNaN(val) && !isNaN(parseFloat(val)))) {
              isNumeric = true;
            }
            break;
          }
        }
      }

      columnStyles[idx] = { halign: isNumeric ? 'right' : 'left' };
    });

    autoTable(doc, {
      startY: y,
      head: [headers],
      body: tableData,
      margin: { left: margin, right: margin },
      styles: { fontSize: 7.5, font: 'helvetica', cellPadding: 3, textColor: colorTextDark },
      headStyles: { fillColor: colorPrimary, textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [252, 253, 254] },
      tableLineColor: colorBorder,
      tableLineWidth: 0.1,
      columnStyles: columnStyles,
    });
  }

  // --- RUNNING FOOTERS LOOP ---
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    // Draw running elegant footer line
    doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
    doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
    
    // Left: Company name & Report title
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(colorTextMuted[0], colorTextMuted[1], colorTextMuted[2]);
    doc.text(`${companyName || ''}  |  ${title || 'Report'}`, margin, pageHeight - 8);
    
    // Center tagline or generated time
    const footGeneratedStr = `Generated on ${currentDateStr} at ${currentTimeStr}`;
    doc.text(footGeneratedStr, pageWidth / 2, pageHeight - 8, { align: 'center' });
    
    // Page numbering
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
  }

  doc.save(`${filename}.pdf`);
}

/**
 * Export report data to Excel (.xlsx).
 * @param {Object} options - Same shape as exportReportToPdf
 * @param {string} [options.sheetName='Report'] - Sheet name (max 31 chars)
 */
export function exportReportToExcel({
  title,
  companyName,
  period,
  columns = [],
  rows = [],
  summaryItems = [],
  filename = 'report',
  sheetName = 'Report',
}) {
  const wb = XLSX.utils.book_new();

  const wsData = [];

  if (title) wsData.push([title]);
  if (companyName) wsData.push([companyName]);
  if (period) wsData.push([`Period: ${period}`]);
  wsData.push([]);

  if (summaryItems.length > 0) {
    wsData.push(['Summary']);
    summaryItems.forEach((s) => wsData.push([s.label, s.value]));
    wsData.push([]);
  }

  if (columns.length > 0) {
    wsData.push(columns.map((c) => c.label));
    rows.forEach((row) => {
      wsData.push(columns.map((col) => {
        const v = row[col.id];
        if (v == null || v === '') return '';
        if (typeof v === 'number') return v;
        return String(v);
      }));
    });
  }

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  const colWidths = columns.length
    ? columns.map((_, i) => {
        const maxLen = Math.max(
          (columns[i].label || '').length,
          ...rows.map((r) => String(r[columns[i].id] ?? '').length)
        );
        return { wch: Math.min(Math.max(maxLen + 1, 10), 50) };
      })
    : [{ wch: 20 }];
  ws['!cols'] = colWidths;

  const sheetNameSafe = (sheetName || 'Report').slice(0, 31);
  XLSX.utils.book_append_sheet(wb, ws, sheetNameSafe);
  XLSX.writeFile(wb, `${filename}.xlsx`);
}
